// AM dashboard, "Movers": four tables over the WHOLE book (Positive / Negative x
// Meta / Google). CPL over the past 7 days vs the 7 before. PURE.
//
// INCLUSION: an account is listed when it has at least MOVER_MIN_LEADS leads in
// BOTH weeks, OR it has a zero-lead transition. leads -> 0 is ALWAYS a negative
// move ("No leads"); 0 -> some is a positive move ("Leads back")
// (advertisingHome/noLeads.ts, classifyMover: the one rule). Everything else is
// left out, and the dashboard says so on screen (`leftOut` counts them).

import type { AdChannel, HomeAccount, SpineDay } from '../../types/advertisingHome.js';
import { classifyMover, type MoverKind } from '../advertisingHome/noLeads.js';
import { addDaysYmd, sumWindow } from '../advertisingHome/spine.js';

export const MOVER_MIN_LEADS = 5;
export const MOVER_WINDOW_DAYS = 7;

export interface MoverRow {
  accountId: string;
  name: string;
  currency: string;
  kind: MoverKind;
  leadsBefore: number;
  leadsAfter: number;
  /** null when the week had no leads (CPL undefined, never $0). */
  cplBefore: number | null;
  cplNow: number | null;
  /** Signed ratio (now ÷ before - 1) for cpl_change; null for transitions. */
  change: number | null;
  /** Spend this week, shown beside "No leads". */
  spendNow: number;
}

export interface MoversResult {
  positive: MoverRow[];
  negative: MoverRow[];
  /** Accounts running this channel but excluded by the volume rule. */
  leftOut: number;
}

export function buildMovers(input: {
  accounts: readonly HomeAccount[];
  rows: readonly SpineDay[];
  channel: AdChannel;
  /** The channel's last complete loaded day. */
  lastDay: string | null;
}): MoversResult {
  const empty: MoversResult = { positive: [], negative: [], leftOut: 0 };
  if (input.lastDay === null) return empty;
  const nowFrom = addDaysYmd(input.lastDay, -(MOVER_WINDOW_DAYS - 1));
  const prevTo = addDaysYmd(nowFrom, -1);
  const prevFrom = addDaysYmd(prevTo, -(MOVER_WINDOW_DAYS - 1));

  const byAccount = new Map<string, SpineDay[]>();
  for (const r of input.rows) {
    if (r.channel !== input.channel) continue;
    const list = byAccount.get(r.accountId);
    if (list) list.push(r);
    else byAccount.set(r.accountId, [r]);
  }

  const positive: MoverRow[] = [];
  const negative: MoverRow[] = [];
  let leftOut = 0;
  for (const a of input.accounts) {
    if (!(input.channel === 'meta' ? a.runsMeta : a.runsGoogle)) continue;
    const days = byAccount.get(a.id) ?? [];
    const now = sumWindow(days, nowFrom, input.lastDay);
    const before = sumWindow(days, prevFrom, prevTo);
    const currency = now.currency ?? before.currency;
    if (currency === null) continue;
    const pick = (w: typeof now): { spend: number; leads: number } => ({
      spend: w.spendByCurrency.find((x) => x.currency === currency)?.amount ?? 0,
      leads: w.leadsByCurrency.find((x) => x.currency === currency)?.amount ?? 0,
    });
    const n = pick(now);
    const b = pick(before);
    const kind = classifyMover(b, n);
    if (kind === null) continue;
    if (kind === 'cpl_change' && (n.leads < MOVER_MIN_LEADS || b.leads < MOVER_MIN_LEADS)) {
      leftOut += 1;
      continue;
    }
    const cplNow = n.leads > 0 && n.spend > 0 ? n.spend / n.leads : null;
    const cplBefore = b.leads > 0 && b.spend > 0 ? b.spend / b.leads : null;
    const change =
      kind === 'cpl_change' && cplNow !== null && cplBefore !== null && cplBefore > 0
        ? cplNow / cplBefore - 1
        : null;
    const row: MoverRow = {
      accountId: a.id,
      name: a.name,
      currency,
      kind,
      leadsBefore: b.leads,
      leadsAfter: n.leads,
      cplBefore,
      cplNow,
      change,
      spendNow: n.spend,
    };
    if (kind === 'leads_gone') negative.push(row);
    else if (kind === 'leads_back') positive.push(row);
    else if (change !== null && change < 0) positive.push(row);
    else if (change !== null && change > 0) negative.push(row);
  }
  // Transitions first (they are the loudest moves), then by the size of the CPL move.
  const rank = (r: MoverRow): number => (r.kind === 'cpl_change' ? 1 : 0);
  negative.sort(
    (x, y) =>
      rank(x) - rank(y) ||
      (x.kind === 'leads_gone' ? y.spendNow - x.spendNow : (y.change ?? 0) - (x.change ?? 0)) ||
      x.name.localeCompare(y.name),
  );
  positive.sort(
    (x, y) =>
      rank(x) - rank(y) ||
      (x.kind === 'leads_back' ? y.leadsAfter - x.leadsAfter : (x.change ?? 0) - (y.change ?? 0)) ||
      x.name.localeCompare(y.name),
  );
  return { positive, negative, leftOut };
}
